import type { ResolverTag } from '@ps/web-core-enums';
import type { FetchTagPayload } from '@ps/web-core-store';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePrevious from '@swf/core/hooks/usePrevious';
import type { Dispatch } from '@swf/core/store';
import { getState, select } from '@swf/core/store';

import useIsMounted from './useIsMounted';

export type Options = Pick<FetchTagPayload, 'tokenType' | 'query' | 'brand' | 'site'>;

const useResolver = (tag?: ResolverTag | string, options: Options = {}): string => {
  const brand = useSelector(select.cms.getBrandName);
  const site = useSelector(select.cms.getSiteName);
  const { resolver } = useDispatch<Dispatch>();
  const [url, setUrl] = useState('');
  const previousTag = usePrevious(tag);
  const previousBrand = usePrevious(brand);
  const isMounted = useIsMounted();

  const fetchResolverTag = useCallback(
    async (tag: ResolverTag | string, options: Options) => {
      await resolver.fetchTag({ tag, ...options });
      const resolvedTag = select.resolver.getTag(getState(), tag);

      if (resolvedTag && isMounted()) {
        setUrl(resolvedTag?.url);
      }
    },
    [isMounted, resolver]
  );

  useEffect(() => {
    if (tag && (!url || tag !== previousTag || brand !== previousBrand)) {
      fetchResolverTag(tag, options);
    }
  }, [fetchResolverTag, tag, url, site, brand, previousTag, options, previousBrand]);

  return url;
};

export default useResolver;
